import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Stack, Divider, Typography, Alert, Box, IconButton, Tooltip } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import ClearIcon from "@mui/icons-material/Clear";
import WarningIcon from "@mui/icons-material/WarningAmberRounded";
import { DateTimePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFnsV3";
import moment from "moment";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { dateLocales } from "../../../i18n";
import inOutScanOverrideService from "../../../services/inOutScanOverride/InOutScanOverride";
import { AUTO_SCHEDULE_AUTHOR, EMPTY_CHARS, roundDateToNearestMin } from "../../../pages/landing/useTimesheetScans";
import { theme } from "../../../styles/themes/mainTheme";

type ScanType = "start" | "end";

const formatDateTime = (date?: Date) => date ? moment.utc(date).local().format("llll") : EMPTY_CHARS;
const dateToUtc = (date: Date) => moment(date).utc().toDate();

export interface IOverrideUpdatePayload {
  userId: number;
  inOutCode: string;
  originalTimestamp: Date;
  overrideTimestamp?: Date;
}

export interface IInlineTimestampCellProps {
  userId: number;
  code: string;
  originalStart: Date;
  originalEnd?: Date;
  overridenStart?: Date;
  overridenEnd?: Date;
  overridenStartAuthor?: string;
  overridenEndAuthor?: string;
  isEndAutoGenerated?: boolean;
  isStartLate?: boolean;
  isEndEarly?: boolean;
  onOverrideUIUpdate: ({ userId, inOutCode, originalTimestamp, overrideTimestamp }: IOverrideUpdatePayload) => void;
  canEdit: boolean;
}

const InlineTimestampCell = ({ userId, code, originalStart, originalEnd, overridenStart, overridenEnd, overridenStartAuthor, overridenEndAuthor, isEndAutoGenerated, isStartLate, isEndEarly, onOverrideUIUpdate, canEdit }: IInlineTimestampCellProps) => {
  const { t, i18n } = useTranslation();
  const [dialogState, setDialogState] = useState<{
    open: boolean;
    target: ScanType;
    value: Date;
  } | null>(null);

  const [error, setError] = useState<string | null>(null);
  const [isConfirmDeleteOpen, setIsConfirmDeleteOpen] = useState(false);

  const accent = "primary.main";

  const roundedStart = useMemo(() => roundDateToNearestMin(originalStart), [originalStart]);
  const roundedEnd = useMemo(() => originalEnd ? roundDateToNearestMin(originalEnd) : undefined, [originalEnd]);

  const onEdit = (scanType: ScanType) => {
    if (!canEdit) return;
    if (scanType === "end" && !roundedEnd) return;

    const effective = scanType === "start"
      ? (overridenStart || roundedStart)
      : (overridenEnd || roundedEnd!);

    setDialogState({ open: true, target: scanType, value: moment.utc(effective).local().toDate() });
    setError(null);
  };

  const onValidateDate = (scanType: ScanType, localValue: Date): string | null => {
    const selectedUtc = dateToUtc(localValue);

    const startEffUtc = dateToUtc((overridenStart || roundedStart));
    const endEffUtc = roundedEnd ? dateToUtc((overridenEnd || roundedEnd)) : undefined;

    if (scanType === "start" && endEffUtc && selectedUtc > endEffUtc) {
      return t("timesheet.errors.validation.startAfterEnd");
    }
    if (scanType === "end" && selectedUtc < startEffUtc) {
      return t("timesheet.errors.validation.endBeforeStart");
    }

    return null;
  };

  const onSave = async () => {
    if (!dialogState) return;

    const error = onValidateDate(dialogState.target, dialogState.value);
    if (error) return setError(error);

    try {
      await inOutScanOverrideService.Upsert({
        userId,
        inOutCode: code,
        originalTimestamp: dialogState.target === "start" ? originalStart : originalEnd!,
        overrideTimestamp: dateToUtc(dialogState.value),
      });

      onOverrideUIUpdate({
        userId,
        inOutCode: code,
        originalTimestamp: dialogState.target === "start" ? originalStart : originalEnd!,
        overrideTimestamp: dateToUtc(dialogState.value),
      });

      setDialogState(null);
    } catch (e: any) {
      setError(e?.message || t("timesheet.errors.generic"));
    }
  };

  const onDelete = async () => {
    if (!dialogState) return;

    try {
      await inOutScanOverrideService.Delete({
        userId,
        inOutCode: code,
        originalTimestamp: dialogState.target === "start" ? originalStart : originalEnd!,
      });

      onOverrideUIUpdate({
        userId,
        inOutCode: code,
        originalTimestamp: dialogState.target === "start" ? originalStart : originalEnd!,
        overrideTimestamp: undefined,
      });

      setIsConfirmDeleteOpen(false);
      setDialogState(null);
    } catch (e: any) {
      setIsConfirmDeleteOpen(false);
      setError(e?.message || t("timesheet.errors.generic"));
    }
  };

  const Line = (scanType: ScanType, originalDate?: Date, overrideDate?: Date) => (
    <Box className="line">
      <Box
        sx={{
          position: "relative",
          display: "flex",
          alignItems: "center",
          width: "fit-content",
        }}
      >

        <Box sx={{ display: "flex", alignItems: "center", flexDirection: "column", }}>
          <Box sx={{ color: isEndAutoGenerated && scanType === "end" ? theme.palette.error.main : "inherit" }}>
            {formatDateTime(originalDate)}
          </Box>
          {overrideDate && originalDate && (
            <Box sx={{ color: accent }}>
              {formatDateTime(overrideDate)}
            </Box>
          )}
        </Box>

        {canEdit && originalDate && moment(originalDate).isValid() && (
          <Box
            sx={{
              position: "absolute",
              top: 0, right: -40, height: "100%",
              display: "none", alignItems: "center",
              ".line:hover &": { display: "flex" },
            }}
          >
            <Tooltip title={t("timesheet.actions.edit")}>
              <IconButton size="small" color="primary" onClick={() => onEdit(scanType)}>
                <EditIcon fontSize="inherit" />
              </IconButton>
            </Tooltip>
          </Box>
        )}

        {isStartLate && scanType === "start" && (
          <Box
            sx={{
              position: "absolute",
              top: 0, left: -30, height: "100%",
              display: "flex", alignItems: "center",
            }}
          >
            <Tooltip title={t("timesheet.arrivedLateTooltip")}>
              <WarningIcon fontSize="small" sx={{ color: theme.palette.warning.main }} />
            </Tooltip>
          </Box>
        )}
        {isEndEarly && scanType === "end" && (
          <Box
            sx={{
              position: "absolute",
              top: 0, left: -30, height: "100%",
              display: "flex", alignItems: "center",
            }}
          >
            <Tooltip title={t("timesheet.leftEarlyTooltip")}>
              <WarningIcon fontSize="small" sx={{ color: theme.palette.warning.main }} />
            </Tooltip>
          </Box>
        )}
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, }}>
      {Line("start", roundedStart, overridenStart)}
      {Line("end", roundedEnd, overridenEnd)}

      <Dialog open={!!dialogState?.open} onClose={() => setDialogState(null)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {dialogState?.target === "start" ? t("timesheet.editStart") : t("timesheet.editEnd")}
        </DialogTitle>

        <DialogContent dividers>
          <Stack spacing={2}>
            {error && <Alert severity="error">{error}</Alert>}

            <Stack direction="row" spacing={3} divider={<Divider flexItem orientation="vertical" />}>
              <Stack spacing={0.5}>
                <Typography variant="caption" color="text.secondary">{t("timesheet.original")}</Typography>
                <Typography>{dialogState?.target === "start" ? formatDateTime(roundedStart) : formatDateTime(roundedEnd)}</Typography>
              </Stack>

              <Stack spacing={0.5}>
                <Typography variant="caption" color="text.secondary">{t("timesheet.currentEffective")}</Typography>
                <Typography color="primary">
                  {dialogState?.target === "start" ? formatDateTime(overridenStart || roundedStart) : formatDateTime(overridenEnd || roundedEnd)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {(() => {
                    const isStart = dialogState?.target === "start";
                    const hasOverride = isStart ? !!overridenStart : !!overridenEnd;
                    if (!hasOverride) return null;

                    const author = isStart ? overridenStartAuthor : overridenEndAuthor;
                    return author === AUTO_SCHEDULE_AUTHOR ? t("timesheet.modifiedBySchedule") : t("timesheet.modifiedBy", { name: author });
                  })()}
                </Typography>
              </Stack>
            </Stack>

            <LocalizationProvider adapterLocale={dateLocales.get(i18n.language)} dateAdapter={AdapterDateFns}>
              <DateTimePicker
                label={t("timesheet.newDateTime")}
                value={dialogState?.value || null}
                onChange={(date) => date && setDialogState(state => state ? { ...state, value: date } : state)}
                ampm={false}
                slotProps={{ textField: { fullWidth: true } }}
              />
            </LocalizationProvider>

            <Typography variant="body2" color="text.secondary">
              {dialogState?.target === "start"
                ? t("timesheet.validationHintStart")
                : t("timesheet.validationHintEnd")}
            </Typography>
          </Stack>
        </DialogContent>

        <DialogActions>
          {(dialogState?.target === "start" ? overridenStart && overridenStartAuthor !== AUTO_SCHEDULE_AUTHOR
            : overridenEnd && overridenEndAuthor !== AUTO_SCHEDULE_AUTHOR) && (
              <Button color="error" onClick={() => setIsConfirmDeleteOpen(true)}>
                {t("timesheet.actions.delete")}
              </Button>
            )}
          <Button onClick={() => setDialogState(null)}>{t("timesheet.actions.cancel")}</Button>
          <Button variant="contained" onClick={onSave}
            disabled={
              dialogState?.target === "start" ?
                moment(dialogState.value).isSame(roundedStart) || moment(dialogState.value).isSame(overridenStart)
                : moment(dialogState?.value).isSame(roundedEnd) || moment(dialogState?.value).isSame(overridenEnd)
            }>
            {t("timesheet.actions.save")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={isConfirmDeleteOpen} onClose={() => setIsConfirmDeleteOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>
          {t("timesheet.confirmDeleteTitle")}
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2">
            {t("timesheet.confirmDeleteMessage")}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsConfirmDeleteOpen(false)}>{t("timesheet.actions.cancel")}</Button>
          <Button color="error" variant="contained" onClick={onDelete} startIcon={<ClearIcon />}>
            {t("timesheet.actions.delete")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );

};

export default InlineTimestampCell;